import React, { useState, useEffect, useRef } from 'react';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
  IonAlert,
  IonText,
  IonIcon,
} from '@ionic/react';
import { arrowBack, checkmark, checkmarkOutline, close, closeOutline, pencilOutline } from 'ionicons/icons';
import { PlanningGroup } from '@/services/PlanningService';
import { FileAttachment } from '@/types/model/inspection-modal';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import AttachmentsSection from '@/components/common/AttachmentsSection';
import ModalFooter from '@/components/common/ModalFooter';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import permissionService from '@/services/PermissionService';
import { Capacitor } from '@capacitor/core';
import { apiService } from '@/services/ApiService';
import {
  buildPlanningLineItemIdentifier,
  mapPlanningAttachments,
  validateLineItemForm,
} from '@/services/util/planningUtils';

export interface LineItemFormData {
  lineItem: string;
  title: string;
  groupId: number;
  paragraph: string;
  subParagraph: string;
  remainder: string;
  majorRequirement: string;
  relatedInfo: string;
  estimatedQuantity: string;
  performanceStandard: string;
}

export interface LineItemInitialData {
  id?: number;
  lineItem?: string;
  lastUpdated?: string;
  title?: string;
  groupId?: number;
  paragraph?: string;
  subParagraph?: string;
  remainder?: string;
  majorRequirement?: string;
  relatedInfo?: string;
  estimatedQuantity?: string;
  performanceStandard?: string;
  modificationStatus?: string;
  attachmentCount?: number;
  isActive?: boolean;
  attachments?: Array<{
    Id?: number;
    Description: string;
    Link: string;
    FileId?: number;
    Filename?: string;
  }>;
}

export interface LineItemSaveData {
  mode: 'add' | 'edit';
  formData: LineItemFormData;
  attachments: FileAttachment[];
  originalAttachments: FileAttachment[];
  initialData?: LineItemInitialData;
}

interface LineItemModalProps {
  isOpen: boolean;
  mode: 'add' | 'view' | 'edit';
  onClose: () => void;
  onSave: (saveData: LineItemSaveData) => Promise<void>;
  onRefresh?: () => Promise<void>;
  initialData?: LineItemInitialData;
  availableGroups: PlanningGroup[];
  existingFiles: FileResponse[];
}

const LineItemModal: React.FC<LineItemModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSave,
  onRefresh,
  initialData,
  availableGroups,
  existingFiles,
}) => {
  const [formData, setFormData] = useState<LineItemFormData>({
    lineItem: '',
    title: '',
    groupId: 0,
    paragraph: '',
    subParagraph: '',
    remainder: '',
    majorRequirement: '',
    relatedInfo: '',
    estimatedQuantity: '',
    performanceStandard: '',
  });

  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [internalMode, setInternalMode] = useState<'add' | 'view' | 'edit'>(mode);
  const isMobilePlatform = Capacitor.getPlatform() !== 'web';
  const [originalFormData, setOriginalFormData] = useState(formData);
  const [showActionConfirmation, setShowActionConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: 'activate' | 'deactivate'; label: string } | null>(null);
  const [actionReason, setActionReason] = useState('');
  const forceClosingRef = useRef(false);

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      forceClosingRef.current = false;
      setInternalMode(mode);
      if (initialData) {
        const newFormData = {
          lineItem: initialData.lineItem || '',
          title: initialData.title || '',
          groupId: initialData.groupId || 0,
          paragraph: initialData.paragraph || '',
          subParagraph: initialData.subParagraph || '',
          remainder: initialData.remainder || '',
          majorRequirement: initialData.majorRequirement || '',
          relatedInfo: initialData.relatedInfo || '',
          estimatedQuantity: initialData.estimatedQuantity || '',
          performanceStandard: initialData.performanceStandard || '',
        };
        setFormData(newFormData);
        setOriginalFormData(newFormData); // Store original for cancel functionality

        // Convert existing attachments to FileAttachment format
        if (initialData.attachments && initialData.attachments.length > 0) {
          setAttachments(mapPlanningAttachments(initialData.attachments));
        } else {
          setAttachments([]);
        }
      } else {
        // Reset to empty form for add mode
        const emptyFormData = {
          lineItem: '',
          title: '',
          groupId: 0,
          paragraph: '',
          subParagraph: '',
          remainder: '',
          majorRequirement: '',
          relatedInfo: '',
          estimatedQuantity: '',
          performanceStandard: '',
        };
        setFormData(emptyFormData);
        setOriginalFormData(emptyFormData);
        setAttachments([]);
      }
      setValidationErrors([]);
    }
  }, [isOpen, initialData, mode]);

  const hasChanges = (): boolean => {
    if (!initialData && internalMode === 'add') {
      // For add mode, check if any field has data
      return Object.values(formData).some(value => value !== '' && value !== 0) || attachments.length > 0;
    }

    if (initialData && internalMode === 'edit') {
      // For edit mode, compare with original values
      const hasFormChanges = 
        formData.lineItem !== originalFormData.lineItem ||
        formData.title !== originalFormData.title ||
        formData.groupId !== originalFormData.groupId ||
        formData.paragraph !== originalFormData.paragraph ||
        formData.subParagraph !== originalFormData.subParagraph ||
        formData.remainder !== originalFormData.remainder ||
        formData.majorRequirement !== originalFormData.majorRequirement ||
        formData.relatedInfo !== originalFormData.relatedInfo ||
        formData.estimatedQuantity !== originalFormData.estimatedQuantity ||
        formData.performanceStandard !== originalFormData.performanceStandard;

      const hasAttachmentChanges = attachments.length !== (initialData.attachments?.length || 0);

      return hasFormChanges || hasAttachmentChanges;
    }

    return false;
  };

  const validateForm = (): string[] => validateLineItemForm(formData);

  const handleClose = () => {
    if (forceClosingRef.current) {
      return;
    }

    if (hasChanges() && internalMode !== 'view') {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirmClose(false);
    forceClosingRef.current = true;
    onClose();
  };

  const handleEdit = () => {
    setInternalMode('edit');
  };

  const handleCancel = () => {
    if (hasChanges()) {
      setShowConfirmClose(true);
    } else {
      // Revert to original data and go back to view mode
      setFormData(originalFormData);
      setInternalMode('view');
    }
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);
    setValidationErrors([]);

    try {
      // Find the selected group to get its sectionId
      const selectedGroup = availableGroups.find(group => group.id === formData.groupId);
      if (!selectedGroup) {
        throw new Error('Selected group not found');
      }

      await onSave({
        mode: internalMode === 'add' ? 'add' : 'edit',
        formData,
        attachments,
        originalAttachments: initialData?.attachments ? mapPlanningAttachments(initialData.attachments) : [],
        initialData,
      });

      handleForceClose();
    } catch (error) {
      console.error('Error saving line item:', error);
      setValidationErrors([error instanceof Error ? error.message : 'Failed to save line item. Please try again.']);
    } finally {
      setIsLoading(false);
    }
  };

  const getLineItemIdentifier = (): string => {
    const groupIdentifier = availableGroups.find(group => group.id === formData.groupId)?.groupIdentifier || '';
    return buildPlanningLineItemIdentifier(
      groupIdentifier,
      formData.paragraph,
      formData.subParagraph,
      formData.remainder,
    );
  };

  // Attachment management functions
  const addAttachment = (type: 'local-file' | 'existing-resource' | 'link' | 'image') => {
    if (type === 'image' && !isMobilePlatform) {
      return;
    }

    const newAttachment: FileAttachment = {
      type,
      index: attachments.length,
    };
    setAttachments([...attachments, newAttachment]);
  };

  const handleAttachmentDelete = (_itemId: number, index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleFileUpdate = (_itemId: number, index: number, file: File) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], file, fileName: file.name, size: file.size };
    setAttachments(updated);
  };

  const handleLinkUpdate = (_itemId: number, index: number, link: string) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], link };
    setAttachments(updated);
  };

  const handleExistingFileSelect = (_itemId: number, index: number, fileId: string) => {
    const updated = [...attachments];
    const selectedFile = existingFiles.find(f => f.Id.toString() === fileId);
    updated[index] = { 
      ...updated[index], 
      fileId, 
      fileName: selectedFile?.Name 
    };
    setAttachments(updated);
  };

  const handleDescriptionUpdate = (_itemId: number, index: number, description: string) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], description };
    setAttachments(updated);
  };

  const getReadOnlyStyle = () => {
    if (internalMode === 'view') {
      return {
        '--background': 'var(--ion-color-light)',
        '--color': 'var(--ion-color-medium-shade)',
        '--border-color': 'var(--ion-color-light-shade)',
        '--placeholder-color': 'var(--ion-color-medium-tint)',
        cursor: 'default',
        opacity: '0.8'
      };
    }
    return {};
  };

  const canShowLineItemActions = Boolean(initialData?.id) && permissionService.canActivateLineItem();

  const isActionDisabled = (action: 'activate' | 'deactivate'): boolean => {
    if (!initialData?.id) return true;
    if (internalMode === 'edit' || isLoading) return true;
    if (action === 'activate' && initialData.isActive === true) return true;
    if (action === 'deactivate' && initialData.isActive === false) return true;
    return false;
  };

  const handleActionClick = (action: 'activate' | 'deactivate') => {
    const actionLabels = {
      activate: 'Activate',
      deactivate: 'Inactivate',
    };

    setPendingAction({ type: action, label: actionLabels[action] });
    setActionReason('');
    setShowActionConfirmation(true);
  };

  const handleActionCancel = () => {
    setShowActionConfirmation(false);
    setPendingAction(null);
    setActionReason('');
  };

  const handleActionConfirm = async () => {
    if (!pendingAction || !initialData?.id) return;

    if (pendingAction.type === 'deactivate' && !actionReason.trim()) {
      setToastMessage('Reason is required to deactivate a line item');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    setShowActionConfirmation(false);

    try {
      if (pendingAction.type === 'activate') {
        await apiService.activateLineItem(initialData.id);
        setToastMessage('Line item activated successfully');
      } else {
        await apiService.inactivateLineItem(initialData.id, actionReason.trim());
        setToastMessage('Line item deactivated successfully');
      }

      setToastVariant('success');
      setShowToast(true);
      if (onRefresh) {
        try {
          await onRefresh();
        } catch (refreshError) {
          console.error('Failed to refresh planning data after line item action:', refreshError);
        }
      }
      onClose();
    } catch (error) {
      console.error('Line item action failed:', error);
      setToastMessage(`Failed to ${pendingAction.label.toLowerCase()} line item`);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false);
      setPendingAction(null);
      setActionReason('');
    }
  };

  return (
    <>
      <IonModal 
        isOpen={isOpen} 
        onDidDismiss={handleClose}
        style={{
          '--width': 'min(80vw, 1400px)',
          '--max-width': '1400px',
          '--height': '90vh'
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{ internalMode === 'add' ? 'Add Line Item' : formData.lineItem + ' ' + formData.title }</IonTitle>
            <IonButtons slot="end">
              {internalMode === 'view' && permissionService.canEditLineItem() && (
                <IonButton fill="clear" onClick={handleEdit}>
                  <IonIcon icon={pencilOutline} />
                </IonButton>
              )}
              <IonButton onClick={handleClose} fill="clear">
                <IonIcon icon={close} />
              </IonButton>

            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          {canShowLineItemActions && (
            <IonCard style={{ width: '100%', maxWidth: '100%' }}>
              <IonCardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <IonButton
                      fill="clear"
                      size="default"
                      disabled={isActionDisabled('activate')}
                      onClick={() => handleActionClick('activate')}
                      title="Activate"
                    >
                      <IonIcon
                        icon={checkmarkOutline}
                        color={isActionDisabled('activate') ? 'medium' : 'success'}
                      />
                    </IonButton>

                    <IonButton
                      fill="clear"
                      size="default"
                      disabled={isActionDisabled('deactivate')}
                      onClick={() => handleActionClick('deactivate')}
                      title="Inactivate"
                    >
                      <IonIcon
                        icon={closeOutline}
                        color={isActionDisabled('deactivate') ? 'medium' : 'warning'}
                      />
                    </IonButton>
                  </div>

                  {internalMode === 'edit' && (
                    <IonText color="medium" style={{ fontSize: '0.85rem' }}>
                      
                      <p style={{ fontStyle: 'italic' }}>Cancel edit to enable actions</p>
                    </IonText>
                  )}

                  {showActionConfirmation && pendingAction && (
                    <div
                      style={{
                        padding: '12px 16px',
                        backgroundColor: 'var(--ion-color-light)',
                        borderRadius: '8px',
                        border: '1px solid var(--ion-color-medium)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <IonText>
                        <span style={{ fontWeight: 'bold' }}>Confirm {pendingAction.label}</span>
                      </IonText>

                      {pendingAction.type === 'deactivate' && (
                        <IonInput
                          value={actionReason}
                          onIonInput={(e) => setActionReason(e.detail.value ?? '')}
                          placeholder="Enter reason"
                          style={{ minWidth: '220px' }}
                        />
                      )}

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <IonButton size="small" fill="solid" color="primary" onClick={handleActionConfirm}>
                          OK
                        </IonButton>
                        <IonButton size="small" fill="outline" color="medium" onClick={handleActionCancel}>
                          Cancel
                        </IonButton>
                      </div>
                    </div>
                  )}
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* Line Item Identifier Display */}
          {getLineItemIdentifier() && (
            <IonItem style={{ 
              marginBottom: '16px',
              ...(internalMode === 'view' ? {
                '--background': 'var(--ion-color-light-tint)',
                '--border-color': 'var(--ion-color-light-shade)'
              } : {})
            }}>
              <IonLabel>
                <h3>Line Item Identifier</h3>
                <p style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--ion-color-primary)' }}>
                  {getLineItemIdentifier()}
                </p>
              </IonLabel>
            </IonItem>
          )}

          {/* Title Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Title *</IonLabel>
            <IonInput
              value={formData.title}
              onIonInput={(e) => setFormData({ ...formData, title: e.detail.value! })}
              placeholder="Enter line item title"
              readonly={internalMode === 'view'}
              maxlength={150}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Group Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Group *</IonLabel>
            <IonSelect
              value={formData.groupId}
              onIonChange={(e) => setFormData({ ...formData, groupId: e.detail.value })}
              placeholder="Select a group"
              disabled={internalMode === 'view' || internalMode === 'edit'}
              style={getReadOnlyStyle()}
            >
              {availableGroups.map((group) => (
                <IonSelectOption key={group.id} value={group.id}>
                  {group.groupIdentifier}. {group.title}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          {/* Paragraph Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Paragraph</IonLabel>
            <IonInput
              value={formData.paragraph}
              onIonInput={(e) => setFormData({ ...formData, paragraph: e.detail.value! })}
              placeholder="Enter paragraph"
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Sub Paragraph Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Sub Paragraph</IonLabel>
            <IonInput
              value={formData.subParagraph}
              onIonInput={(e) => setFormData({ ...formData, subParagraph: e.detail.value! })}
              placeholder="Enter sub paragraph"
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Remainder Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Remainder</IonLabel>
            <IonInput
              value={formData.remainder}
              onIonInput={(e) => setFormData({ ...formData, remainder: e.detail.value! })}
              placeholder="Enter remainder"
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Major Requirement Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Major Requirement</IonLabel>
            <IonTextarea
              value={formData.majorRequirement}
              onIonInput={(e) => setFormData({ ...formData, majorRequirement: e.detail.value! })}
              placeholder="Enter major requirement"
              rows={3}
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Related Info Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Related Info</IonLabel>
            <IonTextarea
              value={formData.relatedInfo}
              onIonInput={(e) => setFormData({ ...formData, relatedInfo: e.detail.value! })}
              placeholder="Enter related information"
              rows={3}
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Estimated Quantity Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Estimated Quantity</IonLabel>
            <IonTextarea
              value={formData.estimatedQuantity}
              onIonInput={(e) => setFormData({ ...formData, estimatedQuantity: e.detail.value! })}
              placeholder="Enter estimated quantity"
              rows={2}
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Performance Standard Field */}
          <IonItem>
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Performance Standard</IonLabel>
            <IonTextarea
              value={formData.performanceStandard}
              onIonInput={(e) => setFormData({ ...formData, performanceStandard: e.detail.value! })}
              placeholder="Enter performance standard"
              rows={2}
              readonly={internalMode === 'view'}
              style={getReadOnlyStyle()}
            />
          </IonItem>

          {/* Attachments Section */}
          <IonItem>
            <AttachmentsSection
              attachments={attachments}
              onAddAttachment={addAttachment}
              onDelete={(_index) => handleAttachmentDelete(0, _index)}
              onFileUpdate={(_index, file) => handleFileUpdate(0, _index, file)}
              onLinkUpdate={(_index, link) => handleLinkUpdate(0, _index, link)}
              onExistingFileSelect={(_index, fileId) => handleExistingFileSelect(0, _index, fileId)}
              onDescriptionUpdate={(_index, description) => handleDescriptionUpdate(0, _index, description)}
              existingFiles={existingFiles}
              readOnly={internalMode === 'view'}
            />
          </IonItem>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              {validationErrors.map((error, index) => (
                <IonText key={index} color="danger">
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>{error}</p>
                </IonText>
              ))}
            </div>
          )}

          {internalMode === 'add' && (
            <div style={{ marginTop: '32px' }}>
              <ModalFooter
                cancelLabel="Cancel"
                actionLabel={isLoading ? 'Saving...' : 'Add Line Item'}
                cancelIcon={arrowBack}
                actionIcon={checkmark}
                onCancel={handleClose}
                onAction={handleSave}
                isCancelDisabled={isLoading}
                isActionDisabled={isLoading}
                isActionLoading={isLoading}
              />
            </div>
          )}

          {internalMode === 'edit' && (
            <div style={{ marginTop: '32px' }}>
              <ModalFooter
                cancelLabel="Cancel"
                actionLabel={isLoading ? 'Saving...' : 'Save Changes'}
                cancelIcon={arrowBack}
                actionIcon={checkmark}
                onCancel={handleCancel}
                onAction={handleSave}
                isCancelDisabled={isLoading}
                isActionDisabled={isLoading}
                isActionLoading={isLoading}
              />
            </div>
          )}
        </IonContent>
      </IonModal>

      {/* Confirmation Alerts */}
      <IonAlert
        isOpen={showConfirmClose}
        onDidDismiss={() => setShowConfirmClose(false)}
        header="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        buttons={[
          {
            text: 'Keep Editing',
            role: 'cancel',
          },
          {
            text: 'Discard Changes',
            handler: () => {
              if (internalMode === 'edit') {
                // Revert to original data and go back to view mode
                setFormData(originalFormData);
                setInternalMode('view');
              } else {
                handleForceClose();
              }
            },
          },
        ]}
      />

      <Toaster
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
    </>
  );
};

export default LineItemModal;